import { useEffect, useState } from 'react'
import { api } from '../lib/api'
import { signInWithSms } from '../hooks/useSession'
import { useI18n } from '../hooks/useI18n'
import { showToast } from '../lib/toast'
import Button from '../components/Button'

/**
 * Phone login — the ONE thing the web does differently from the mini program,
 * which signs in with WeChat.
 *
 * Everything after this point is identical: the same JWT, the same /me, the
 * same onboarding questionnaire. The backend issues the same token for an SMS
 * verify as for a WeChat login, so nothing downstream knows or cares which
 * door someone came through.
 *
 * The tab bar stays visible, so this screen can be left at any moment — the
 * requirement that got the mini program rejected.
 */
const CHINA_MOBILE = /^1[3-9]\d{9}$/

export default function SmsLoginPage({ onDone }: { onDone: () => void }) {
  const { t } = useI18n()
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [cooldown, setCooldown] = useState(0)

  useEffect(() => {
    if (!cooldown) return undefined
    const timer = setTimeout(() => setCooldown((n) => n - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const phoneValid = CHINA_MOBILE.test(phone)

  const sendCode = async () => {
    if (!phoneValid) return showToast({ title: t('sms.invalidPhone'), icon: 'none' })
    setSending(true)
    try {
      await api.sendSmsCode(phone)
      // Cooldown is a client-side courtesy; the backend enforces the real
      // per-number hourly cap, because a determined caller ignores this timer.
      setCooldown(60)
      showToast({ title: t('sms.codeSent'), icon: 'success' })
    } catch (error) {
      showToast({ title: (error as { message?: string })?.message || t('sms.sendFailed'), icon: 'none' })
    } finally { setSending(false) }
  }

  const verify = async () => {
    if (code.trim().length < 4) return showToast({ title: t('sms.enterCode'), icon: 'none' })
    setVerifying(true)
    try {
      await signInWithSms(phone, code.trim())
      onDone()
    } catch (error) {
      showToast({ title: (error as { message?: string })?.message || t('sms.wrongCode'), icon: 'none' })
    } finally { setVerifying(false) }
  }

  return (
    <div className="pt-1">
      <p className="text-2xs uppercase tracking-kicker text-gold">{t('sms.kicker')}</p>
      <h1 className="mt-2 font-display text-hero leading-tight text-ivory">{t('sms.title')}</h1>
      <p className="mt-2 text-13 leading-relaxed text-muted">{t('sms.sub')}</p>

      <div className="mt-6 space-y-3">
        <div className="flex items-stretch gap-2">
          <span className="flex items-center rounded-sm border border-gold-dim px-3 text-sm text-muted">+86</span>
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            maxLength={11}
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            placeholder={t('sms.phonePlaceholder')}
            className="h-12 min-w-0 flex-1 rounded-sm border border-gold-dim bg-card px-3 text-md text-ivory placeholder:text-muted-2 focus:border-gold focus:outline-none"
          />
        </div>

        <div className="flex items-stretch gap-2">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder={t('sms.codePlaceholder')}
            className="h-12 min-w-0 flex-1 rounded-sm border border-gold-dim bg-card px-3 text-md tracking-[0.3em] text-ivory placeholder:tracking-normal placeholder:text-muted-2 focus:border-gold focus:outline-none"
          />
          <button
            type="button"
            onClick={sendCode}
            disabled={sending || cooldown > 0 || !phoneValid}
            className="shrink-0 whitespace-nowrap rounded-sm border border-gold-dim px-4 text-sm text-gold disabled:opacity-45"
          >
            {cooldown > 0 ? `${cooldown}s` : t('sms.sendCode')}
          </button>
        </div>

        <Button loading={verifying} disabled={!phoneValid || code.length < 4} onClick={verify}>
          {t('sms.verify')}
        </Button>

        <p className="pt-1 text-center text-xs leading-relaxed text-muted-2">{t('sms.legal')}</p>
      </div>
    </div>
  )
}
